// Lo que se anota sin señal, guardado en el teléfono hasta que se pueda enviar.
//
// El acopiador va a las fincas a comprar la leche, y ahí es justo donde no hay
// señal. Hasta ahora el recibo se perdía: salía un error y lo tecleado se iba.
//
// ── Por qué ESTO se puede y otras cosas no ──
//
// Un recibo de leche es el caso fácil del trabajo sin señal, y vale la pena
// entender por qué antes de copiar esta idea a otra pantalla:
//
//   · Solo AGREGA. No modifica nada que alguien más pueda estar cambiando.
//   · No necesita saldos para estar bien. El recibo es un hecho suelto: tantos
//     litros, a tal precio, tal día. Se puede anotar sin saber nada más.
//   · No hay conflicto posible. Nadie más va a registrar ese mismo recibo.
//
// Enseñar un saldo sin señal sería otra cosa —puede estar viejo y hacer que se
// cobre mal—, y por eso el resto del programa sigue pidiendo internet.
//
// ── El único peligro de verdad: el recibo duplicado ──
//
// Si el envío se corta DESPUÉS de que el servidor guardó pero ANTES de que
// llegue la respuesta, el teléfono no sabe si quedó y lo vuelve a mandar. Sin
// defensa, eso son dos recibos de la misma leche — y a la finca se le pagaría
// de más.
//
// Por eso cada recibo lleva una llave propia, generada aquí, que viaja con él y
// no cambia entre reintentos. El servidor la usa para reconocerlo. La llave se
// genera UNA VEZ al guardar en la cola, nunca al enviar: generarla al enviar
// sería una llave nueva por intento, que es lo mismo que no tener ninguna.

const CAJON = 'quesera.recibos.pendientes';

// El almacén se recibe por fuera para poder probar esto sin navegador.
const almacenPorDefecto = () =>
  (typeof localStorage !== 'undefined' ? localStorage : null);

function leerCrudo(almacen) {
  const a = almacen || almacenPorDefecto();
  if (!a) return [];
  try {
    const txt = a.getItem(CAJON);
    const lista = txt ? JSON.parse(txt) : [];
    return Array.isArray(lista) ? lista : [];
  } catch (e) {
    // Un cajón corrupto no puede tumbar la pantalla: se empieza de cero. Es
    // preferible perder la cola a que el programa no abra.
    return [];
  }
}

function escribir(almacen, lista) {
  const a = almacen || almacenPorDefecto();
  if (!a) return false;
  try {
    a.setItem(CAJON, JSON.stringify(lista));
    return true;
  } catch (e) {
    return false;   // sin espacio: se avisa arriba, no se pierde en silencio
  }
}

// Una llave que no se repite entre teléfonos ni entre recibos.
export function nuevaLlave() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Sin crypto —navegadores viejos— se arma con la hora y dos números al azar.
  // La hora sola no basta: dos recibos del mismo segundo chocarían.
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}` +
         `-${Math.random().toString(36).slice(2, 10)}`;
}

export function pendientes(almacen) {
  return leerCrudo(almacen);
}

// Guarda un recibo para enviarlo después. Devuelve el recibo con su llave.
export function encolar(recibo, almacen) {
  const lista = leerCrudo(almacen);
  const conLlave = {
    ...recibo,
    clave_local: recibo.clave_local || nuevaLlave(),
    anotado_en: recibo.anotado_en || new Date().toISOString(),
  };
  lista.push(conLlave);
  return escribir(almacen, lista) ? conLlave : null;
}

export function quitar(clave, almacen) {
  const lista = leerCrudo(almacen).filter(r => r.clave_local !== clave);
  escribir(almacen, lista);
  return lista;
}

export function vaciar(almacen) {
  escribir(almacen, []);
}

// Manda lo que haya pendiente, uno por uno y en orden.
//
// `enviar(recibo)` lo pone quien llama: es el que sabe hablar con el servidor.
// Debe devolver algo que se resuelva si quedó guardado y que falle si no.
//
// Se envía DE UNO EN UNO a propósito. En bloque, un recibo malo —una finca que
// se borró, por ejemplo— tumbaría a todos los demás con él, y quedarían
// atrapados sin que nadie sepa cuál era el problema.
export async function enviarPendientes(enviar, almacen) {
  const lista = leerCrudo(almacen);
  let enviados = 0;
  const quedan = [];

  for (const recibo of lista) {
    try {
      await enviar(recibo);
      enviados += 1;
    } catch (e) {
      // Se conserva para el próximo intento. No se cuenta cuántas veces ha
      // fallado ni se descarta nunca: un recibo perdido es plata que la finca
      // entregó y nadie anotó, y eso no se arregla después.
      quedan.push(recibo);
    }
  }

  escribir(almacen, quedan);
  return { enviados, pendientes: quedan.length };
}
